import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { contactInfo } from '../mock';

const navLinks = [
  { to: '/', label: 'Startseite' },
  { to: '/ueber-uns', label: 'Über uns' },
  { to: '/services', label: 'Services' },
  { to: '/wochenmenue', label: 'Wochenmenü' },
  { to: '/speisekarte', label: 'Speisekarte' },
  { to: '/saisonkarte', label: 'Saisonkarte' },
  { to: '/galerie', label: 'Galerie' },
  { to: '/kontakt', label: 'Kontakt' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled || mobileOpen ? 'bg-[#1a1410]/95 backdrop-blur-md shadow-lg' : 'bg-gradient-to-b from-black/70 to-transparent'}`}>
      {/* Top strip with contact */}
      <div className="hidden md:block border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-xs text-amber-100/80">
          <div className="flex items-center gap-6">
            <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2 hover:text-amber-300 transition-colors">
              <Phone size={13} /> {contactInfo.phone}
            </a>
            <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2 hover:text-amber-300 transition-colors">
              <Mail size={13} /> {contactInfo.email}
            </a>
          </div>
          <span className="tracking-widest uppercase">Otto-Herschmann-Gasse 6, 1110 Wien</span>
        </div>
      </div>

      {/* Main nav */}
      <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center font-bold text-[#1a1410] text-lg shadow-lg group-hover:scale-105 transition-transform">KSV</div>
          <div className="leading-tight">
            <div className="text-white font-serif text-lg tracking-wide">Sportkantine</div>
            <div className="text-amber-300/80 text-[10px] tracking-[0.3em] uppercase">Wien 1110</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => {
            const active = location.pathname === l.to;
            return (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className={`relative whitespace-nowrap px-2 xl:px-4 py-2 text-[13px] xl:text-sm font-medium tracking-wide transition-colors ${active ? 'text-amber-300' : 'text-white/90 hover:text-amber-300'}`}
                >
                  {l.label}
                  {active && <span className="absolute left-2 right-2 xl:left-4 xl:right-4 -bottom-0.5 h-[2px] bg-amber-400" />}
                </Link>
              </li>
            );
          })}
        </ul>

        <a href={`tel:${contactInfo.phone}`} className="hidden lg:inline-flex shrink-0 whitespace-nowrap items-center gap-2 px-4 xl:px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-400 text-[#1a1410] font-semibold text-sm shadow-lg hover:shadow-amber-500/30 transition-all">
          <Phone size={14}/> Reservieren
        </a>

        {/* Mobile toggle */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2" aria-label="Menü">
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#1a1410] border-t border-white/10">
          <ul className="px-6 py-4 space-y-1">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className={`block px-3 py-3 rounded-md text-sm font-medium ${location.pathname === l.to ? 'bg-amber-500/10 text-amber-300' : 'text-white/90 hover:bg-white/5'}`}>{l.label}</Link>
              </li>
            ))}
            <li className="pt-3">
              <a href={`tel:${contactInfo.phone}`} className="block text-center px-5 py-3 rounded-full bg-amber-500 text-[#1a1410] font-semibold text-sm">
                Anrufen &amp; reservieren
              </a>
            </li>
            <li className="pt-3 border-t border-white/10 mt-3 space-y-2 text-xs text-amber-100/70">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2"><Phone size={13}/> {contactInfo.phone}</a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2"><Mail size={13}/> {contactInfo.email}</a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
